import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InvoiceService } from '../invoice.service';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmSpinnerComponent,
  HlmAlertComponent,
} from '../../../ui';

export interface EvnExcelDialogData {
  invoiceId: number;
  invoiceNumber: number;
}

@Component({
  selector: 'cmt-evn-excel-dialog',
  imports: [
    ...HLM_DIALOG_SHELL,
    HlmBtnDirective,
    HlmIconComponent,
    HlmSpinnerComponent,
    HlmAlertComponent,
  ],
  template: `
    <hlm-dialog-surface size="sm">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucideFileText" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>EVN Excel — Invoice {{ data.invoiceNumber }}</h2>
          <p hlmDialogDescription>Download the call record as an Excel spreadsheet.</p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        <div class="flex flex-col items-center gap-4 py-4">
          <span class="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
            <hlm-icon name="lucideFileText" size="1.5rem" />
          </span>
          <p class="text-center text-sm text-muted-foreground">
            The Excel file cannot be previewed here. Please download the file.
          </p>
          <button hlmBtn (click)="download()" [disabled]="downloading()">
            @if (downloading()) {
              <hlm-spinner size="16" />
              Downloading…
            } @else {
              <hlm-icon name="lucideDownload" size="0.9rem" />
              Download EVN Excel
            }
          </button>
          @if (errorMsg()) {
            <hlm-alert tone="error">
              <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
              <span>{{ errorMsg() }}</span>
            </hlm-alert>
          }
        </div>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()">Close</button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class EvnExcelDialogComponent {
  readonly data = inject<EvnExcelDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<EvnExcelDialogComponent>);
  private readonly invoiceService = inject(InvoiceService);

  readonly downloading = signal(false);
  readonly errorMsg = signal<string | null>(null);

  download(): void {
    this.downloading.set(true);
    this.errorMsg.set(null);

    this.invoiceService.getEvnExcel(this.data.invoiceId).subscribe({
      next: (blob) => {
        const a = document.createElement('a');
        const url = URL.createObjectURL(blob);
        a.href = url;
        a.download = `EVN_${this.data.invoiceNumber}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        this.downloading.set(false);
      },
      error: () => {
        this.errorMsg.set('Failed to download EVN Excel');
        this.downloading.set(false);
      },
    });
  }
}
